import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { appShellL } from "@/i18n/appShell";
import { APP_BAR_SLOT } from "./AppBarSlot";

/**
 * The top bar of an app screen (never the website's), in three variants:
 * - "solid": back and the screen's title (18/800);
 * - "tab": a tab's large title (Figtree 24), no back: the tab bar leads home;
 * - "over-photo": back as a round white button over a photo, no title.
 * Back goes through the history, or to `fallback` when the screen was opened
 * from a deep link or a notification, with no history to go back to. The
 * arrow turns round in Arabic. `actions` sit at the end.
 */
export default function AppBar({ title, subtitle, actions, fallback = "/", variant = "solid", as: Title = "span" }) {
  const nav = useNavigate();
  const { lang } = useSettings();
  const l = pick(appShellL, lang);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (variant === "tab") {
    return (
      <header className="tk-appbar" data-variant="tab" data-scrolled={scrolled} data-testid="app-bar">
        <Title className="min-w-0 flex-1 truncate font-heading text-heading-xl text-touki-black">{title}</Title>
        {actions}
      </header>
    );
  }

  const back = () => (window.history.state?.idx > 0 ? nav(-1) : nav(fallback, { replace: true }));
  const glass = variant === "over-photo" ? { "data-glass": "" } : {};
  return (
    <header className="tk-appbar" data-variant={variant} data-scrolled={scrolled} data-testid="app-bar">
      <button type="button" className="tk-icon-btn" onClick={back} aria-label={l.appBar.back} data-testid="app-bar-back" {...glass}>
        <ArrowLeft size={24} className="rtl:-scale-x-100" aria-hidden="true" />
      </button>
      {title ? (
        <Title className="min-w-0 flex-1 truncate text-heading-md font-extrabold text-touki-black">
          {title}
          {subtitle ? <span className="block truncate text-caption font-bold text-touki-muted">{subtitle}</span> : null}
        </Title>
      ) : <span className="flex-1" />}
      {actions || <span id={APP_BAR_SLOT} className="tk-appbar-slot" />}
    </header>
  );
}
